import { create } from 'zustand';
import type { ItemType } from '../config/items';

interface PlayerState {
  id: string;
  sessionId?: string;
  nickname: string;
  color: string;
  x: number;
  y: number;
  direction?: number;
  score: number;
  territoryPercent?: number;
  isAlive: boolean;
  trail: { x: number; y: number }[];
  // trail 좌표계: 'world' = 픽셀 좌표 (신규), 'grid' = 타일 좌표 (구버전 클라이언트)
  trailSpace?: 'grid' | 'world';
  // 활성 아이템 효과 (원격 동기화)
  effect?: { type: ItemType; remainingMs: number } | null;
  hasPosition?: boolean;
  lastSeenAt?: number;
  lastSeq?: number;
}

interface GameResult {
  score: number;
  kills: number;
  maxTerritoryPercent: number;
  playTime: number; // seconds
  // 사망 원인 문구 (예: "OOO에게 꼬리를 잡혔어요!")
  deathCause?: string | null;
}

interface GameState {
  score: number;
  kills: number;
  territoryPercent: number;
  maxTerritoryPercent: number;
  gameStartTime: number;
  lastGameResult: GameResult | null;
  localPlayerId: string | null;
  players: Map<string, PlayerState>;
  deathCause: string | null;

  setDeathCause: (cause: string) => void;
  setScore: (score: number) => void;
  addScore: (amount: number) => void;
  addKill: () => void;
  setTerritoryPercent: (percent: number) => void;
  saveGameResult: () => void;
  resetGame: () => void;
  setLocalPlayerId: (id: string) => void;
  addPlayer: (player: PlayerState) => void;
  updatePlayer: (id: string, data: Partial<PlayerState>) => void;
  removePlayer: (id: string) => void;
  getLocalPlayer: () => PlayerState | undefined;
}

export const useGameStore = create<GameState>((set, get) => ({
  score: 0,
  kills: 0,
  territoryPercent: 0,
  maxTerritoryPercent: 0,
  gameStartTime: Date.now(),
  lastGameResult: null,
  localPlayerId: null,
  players: new Map(),
  deathCause: null,

  // 가장 먼저 기록된 사망 원인만 유지 (중복 사망 이벤트로 덮어쓰지 않음)
  setDeathCause: (cause) => set((state) => (
    state.deathCause ? {} : { deathCause: cause }
  )),
  setScore: (score) => set({ score }),
  addScore: (amount) => set((state) => ({ score: state.score + amount })),
  addKill: () => set((state) => ({ kills: state.kills + 1 })),
  setTerritoryPercent: (percent) => set((state) => ({
    territoryPercent: percent,
    maxTerritoryPercent: Math.max(state.maxTerritoryPercent, percent)
  })),
  saveGameResult: () => {
    const state = get();
    const playTime = Math.floor((Date.now() - state.gameStartTime) / 1000);
    set({
      lastGameResult: {
        score: state.score,
        kills: state.kills,
        maxTerritoryPercent: state.maxTerritoryPercent,
        playTime,
        deathCause: state.deathCause
      }
    });
  },
  resetGame: () => set({
    score: 0,
    kills: 0,
    territoryPercent: 0,
    maxTerritoryPercent: 0,
    gameStartTime: Date.now(),
    players: new Map(),
    deathCause: null
  }),
  setLocalPlayerId: (id) => set({ localPlayerId: id }),
  addPlayer: (player) => set((state) => {
    const newPlayers = new Map(state.players);
    newPlayers.set(player.id, player);
    return { players: newPlayers };
  }),
  updatePlayer: (id, data) => set((state) => {
    const newPlayers = new Map(state.players);
    const player = newPlayers.get(id);
    if (player) {
      newPlayers.set(id, { ...player, ...data });
    }
    return { players: newPlayers };
  }),
  removePlayer: (id) => set((state) => {
    const newPlayers = new Map(state.players);
    newPlayers.delete(id);
    return { players: newPlayers };
  }),
  getLocalPlayer: () => {
    const state = get();
    return state.localPlayerId ? state.players.get(state.localPlayerId) : undefined;
  }
}));
